import React from 'react';
import { Box, Typography } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';
import { alpha } from '@mui/material/styles';
import { COLORS, COMPONENT_COLORS } from '../results/ResultsDesignTokens';
import {
    BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
    ReferenceLine, Tooltip, Cell,
} from 'recharts';

export default function ComponentAccuracyChart({ componentAccuracy }) {
    if (!componentAccuracy || componentAccuracy.length === 0) return null;

    const mapeData = componentAccuracy.map(ca => ({
        name: ca.component,
        mape: ca.mape,
        target: ca.target_mape,
        passed: ca.passed,
    }));

    const r2Data = componentAccuracy.map(ca => ({
        name: ca.component,
        r2: ca.r2,
        target: ca.target_r2,
    }));

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* MAPE Chart */}
            <Box>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: COLORS.text.muted,
                    mb: 1,
                }}>
                    MAPE by Component (%)
                </Typography>
                <Box sx={{ height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={mapeData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <XAxis
                                dataKey="name"
                                tick={{ fill: COLORS.text.muted, fontSize: 10, fontFamily: vortxTypography.mono }}
                                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                tickLine={false}
                            />
                            <YAxis
                                tick={{ fill: COLORS.text.muted, fontSize: 10, fontFamily: vortxTypography.mono }}
                                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                tickLine={false}
                            />
                            <Tooltip
                                contentStyle={{
                                    background: COLORS.bg.card,
                                    border: `1px solid ${alpha(COLORS.accent, 0.3)}`,
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.7rem',
                                }}
                            />
                            <Bar dataKey="mape" radius={[4, 4, 0, 0]}>
                                {mapeData.map((entry) => (
                                    <Cell
                                        key={entry.name}
                                        fill={COMPONENT_COLORS[entry.name] || COLORS.accent}
                                        fillOpacity={0.8}
                                    />
                                ))}
                            </Bar>
                            {/* Target line (use max target as reference) */}
                            {mapeData.map(entry => (
                                <ReferenceLine
                                    key={entry.name}
                                    y={entry.target}
                                    stroke={COLORS.text.muted}
                                    strokeDasharray="4 4"
                                    strokeWidth={1}
                                />
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Box>

            {/* R2 Chart */}
            <Box>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: COLORS.text.muted,
                    mb: 1,
                }}>
                    R² by Component
                </Typography>
                <Box sx={{ height: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={r2Data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <XAxis
                                dataKey="name"
                                tick={{ fill: COLORS.text.muted, fontSize: 10, fontFamily: vortxTypography.mono }}
                                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                tickLine={false}
                            />
                            <YAxis
                                domain={[0, 1]}
                                tick={{ fill: COLORS.text.muted, fontSize: 10, fontFamily: vortxTypography.mono }}
                                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                                tickLine={false}
                            />
                            <Tooltip
                                contentStyle={{
                                    background: COLORS.bg.card,
                                    border: `1px solid ${alpha(COLORS.accent, 0.3)}`,
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.7rem',
                                }}
                            />
                            <Bar dataKey="r2" radius={[4, 4, 0, 0]}>
                                {r2Data.map((entry) => (
                                    <Cell
                                        key={entry.name}
                                        fill={COMPONENT_COLORS[entry.name] || COLORS.accent}
                                        fillOpacity={0.8}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Box>
        </Box>
    );
}
